import { create } from "zustand";
import { CommunityViewPrivate, CommunityViewPublic } from "@bbb/model";
import { CommunityActions, CommunityState } from "@interfaces";

const toCommunityViewPublic = (
  community: CommunityViewPrivate,
): CommunityViewPublic => {
  return {
    id: community.id,
    profile: community.profile,
    teamMembers: community.teamMembers,
    createdAt: community.createdAt,
    updatedAt: community.updatedAt,
    participantsCount: community.participantsCount,
    followersCount: community.followersCount,
    totalRewards: community.totalRewards,
  };
};

const mergeCommunities = (
  publicCommunities: CommunityViewPublic[],
  userOwnedCommunities: CommunityViewPrivate[] | null,
): CommunityViewPublic[] => {
  const communityMap = new Map<string, CommunityViewPublic>();

  publicCommunities.forEach((community) => {
    communityMap.set(community.id, community);
  });

  (userOwnedCommunities ?? []).forEach((community) => {
    communityMap.set(community.id, toCommunityViewPublic(community));
  });

  return Array.from(communityMap.values());
};

export const useCommunityStore = create<CommunityState & CommunityActions>()(
  (set) => ({
    selectedCommunity: null,
    selectedCampaign: null,
    userOwnedCommunities: null,
    selectedPrivateCommunity: null,
    communities: [],
    teamMembersByCommunity: {},

    setSelectedCommunity: (community) =>
      set(() => ({ selectedCommunity: community })),
    setSelectedCampaign: (campaign) =>
      set(() => ({ selectedCampaign: campaign })),
    setUserOwnedCommunities: (updater) =>
      set((state) => {
        const next =
          typeof updater === "function"
            ? updater(state.userOwnedCommunities)
            : updater;
        return { userOwnedCommunities: next };
      }),
    setSelectedPrivateCommunity: (updater) =>
      set((state) => {
        const next =
          typeof updater === "function"
            ? updater(state.selectedPrivateCommunity)
            : updater;
        return { selectedPrivateCommunity: next };
      }),
    upsertPrivateCommunity: (community) =>
      set((state) => {
        const existing = state.userOwnedCommunities ?? [];
        const idx = existing.findIndex((c) => c.id === community.id);
        const nextOwned =
          idx === -1
            ? [...existing, community]
            : existing.map((c, i) => (i === idx ? community : c));

        // Keep the public community list in sync with the latest owned data
        const publicView = toCommunityViewPublic(community);
        const communitiesIdx = state.communities.findIndex(
          (c) => c.id === community.id,
        );
        const nextCommunities =
          communitiesIdx === -1
            ? [...state.communities, publicView]
            : state.communities.map((c) =>
                c.id === community.id ? publicView : c,
              );

        // Always promote the freshly upserted community to the active selection.
        const nextPrimary = community;

        return {
          userOwnedCommunities: nextOwned,
          selectedPrivateCommunity: nextPrimary,
          communities: mergeCommunities(nextCommunities, nextOwned),
        };
      }),
    setCommunities: (communities) =>
      set(() => ({
        communities,
      })),
    setTeamMembersForCommunity: (communityId, members) =>
      set((state) => ({
        teamMembersByCommunity: {
          ...state.teamMembersByCommunity,
          [communityId]: members,
        },
      })),
    clearTeamMembersForCommunity: (communityId) =>
      set((state) => {
        const next = { ...state.teamMembersByCommunity };
        delete next[communityId];
        return {
          teamMembersByCommunity: next,
        };
      }),
    clearSelection: () =>
      set(() => ({
        selectedCommunity: null,
        selectedCampaign: null,
      })),
  }),
);
